import {
  SkyBadge,
  SkyButton,
  SkyLink,
  SkyMenu,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import type { TenantView } from "@shared/types/tenants";
import { websiteSchema } from "@shared/types/tenants";
import { TenantResourcesChip } from "@/components/platform/TenantResourcesChip";
import { ENTRA_DIRECTORY_UNSET } from "@/components/platform/TenantDialog";
import { EditIcon } from "@/lib/icons";
import { ROUTES, absolutePath } from "@/lib/routes";
import { formatInstant } from "@/lib/time/displayTime";

/**
 * The tenant's website as a link that opens a new tab and hands the site nothing back
 * (`noopener noreferrer`). Only an https value is a link: every value the API accepts is one,
 * and anything else (a hand-edited row) is shown as text rather than rendered as an href.
 */
function WebsiteLink({ website, deleted }: { website: string | null; deleted: boolean }) {
  if (!website) {
    return (
      <SkyText size={200} color="muted" block>
        No website
      </SkyText>
    );
  }
  // #991: a stored value that fails the rule the server applies is shown as text with a note,
  // never as a link and never rewritten. Edit names the rule in full. A deleted tenant cannot be
  // edited, so its note names no next step.
  if (!websiteSchema.safeParse(website).success) {
    return (
      <div className="flex min-w-0 flex-col">
        <SkyText size={200} color="muted" block truncate title={website}>
          {website}
        </SkyText>
        <SkyText size={200} color="warning" block testId="website-invalid-note">
          {deleted
            ? "Not a valid website."
            : "Not a valid website. Edit the tenant and enter one starting with https://."}
        </SkyText>
      </div>
    );
  }
  const shown = website.replace(/^https:\/\//, "").replace(/\/$/, "");
  return (
    <div className="min-w-0">
      <SkyLink
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        title={website}
        className="inline-flex max-w-full items-center gap-1 break-all"
      >
        {shown}
      </SkyLink>
    </div>
  );
}

/**
 * The status badge's word: a deleted tenant is still `disabled`, and reads Deleted. #921: an
 * active tenant whose own resources are still being set up reads "Setting up", and one whose
 * set-up failed reads "Setup failed", never "Active" beside a Resources chip that says otherwise.
 */
function statusWord(row: TenantView): string {
  if (row.deletedAt) return "Deleted";
  if (row.status !== "active") return "Disabled";
  if (row.resources?.status === "provisioning") return "Setting up";
  return row.resources?.status === "failed" ? "Setup failed" : "Active";
}

function statusColor(row: TenantView): "success" | "danger" | "subtle" | "informative" {
  if (row.deletedAt) return "danger";
  if (row.status !== "active") return "subtle";
  if (row.resources?.status === "provisioning") return "informative";
  return row.resources?.status === "failed" ? "danger" : "success";
}

/** What the grid's cells do, and which optional columns it has. */
export interface TenantColumnsContext {
  zone: string;
  showEntraDirectory: boolean;
  showResources: boolean;
  modeUnknown: boolean;
  onOffboarding: (row: TenantView) => void;
  onEdit: (row: TenantView) => void;
  onConnectors: (row: TenantView) => void;
  onToggle: (row: TenantView) => void;
  onDelete: (row: TenantView) => void;
  onResources: (tenantId: string) => void;
}

export function buildTenantColumns({
  zone,
  showEntraDirectory,
  showResources,
  modeUnknown,
  onOffboarding,
  onEdit,
  onConnectors,
  onToggle,
  onDelete,
  onResources,
}: TenantColumnsContext): SkyDataGridColumn<TenantView>[] {
  return [
    {
      key: "displayName",
      header: "Display name",
      sortable: true,
      width: "1%",
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col">
          <SkyText size={300} weight="medium" block truncate title={row.displayName}>
            {row.displayName}
          </SkyText>
          {row.purpose === "canary" ? (
            // #826: Skypoint's own daily canary tenant, never a customer.
            <div className="flex items-start">
              <SkyBadge appearance="tint" color="informative" testId={`tenant-canary-${row.id}`}>
                Canary
              </SkyBadge>
            </div>
          ) : null}
          <SkyText size={200} color="muted" block>
            Created {formatInstant(row.createdAt, zone, "day")}
          </SkyText>
        </div>
      ),
    },
    {
      key: "name",
      // "Name", the field's own label (walk W18; owner 2026-10-01 23:25): `tenants.name`. The
      // test id keeps its round 5 words. Second, beside the Display name it is the technical half
      // of.
      header: "Name",
      width: "1%",
      renderCell: (_value, row) => (
        <SkyText
          size={200}
          font="monospace"
          block
          truncate
          title={row.name}
          testId={`customer-tenant-slug-${row.id}`}
        >
          {row.name}
        </SkyText>
      ),
    },
    {
      // Its own column since round 6 A1 (it sat under the name in round 5).
      key: "website",
      header: "Website",
      width: "1%",
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col" data-testid={`customer-website-${row.id}`}>
          <WebsiteLink website={row.website} deleted={Boolean(row.deletedAt)} />
        </div>
      ),
    },
    // Only while group access is on: the directory id is its one reader (round 5).
    ...(showEntraDirectory
      ? [
          {
            key: "entraTenantId",
            header: "Entra directory (tenant) ID",
            width: "1%",
            renderCell: (_value: unknown, row: TenantView) =>
              row.entraTenantId ? (
                <SkyText size={200} font="monospace" block wrap className="break-all">
                  {row.entraTenantId}
                </SkyText>
              ) : (
                // A dash said nothing; an unset directory switches directory-group access off for
                // the tenant (round 4 item 15).
                <SkyText size={200} color="muted" block wrap>
                  {ENTRA_DIRECTORY_UNSET}
                </SkyText>
              ),
          } satisfies SkyDataGridColumn<TenantView>,
        ]
      : []),
    {
      key: "instanceCount",
      header: "Instances",
      align: "right",
      width: "1%",
      renderCell: (_value, row) => (
        // The count opens the Instances screen filtered to this tenant. Absolute: App.tsx nests
        // each section, so a bare path would resolve against /admin/tenants.
        <SkyLink
          as={Link}
          href={absolutePath(`${ROUTES.adminInstances}?tenant=${row.id}`)}
          aria-label={`Instances of ${row.displayName}`}
          testId={`customer-instances-${row.id}`}
        >
          <SkyText size={300} font="numeric">
            {row.instanceCount}
          </SkyText>
        </SkyLink>
      ),
    },
    // Only in a deployment that gives tenants their own Azure resources (ADR 0010).
    ...(showResources
      ? [
          {
            key: "resources",
            header: "Resources",
            width: "1%",
            renderCell: (_value: unknown, row: TenantView) =>
              row.resources ? (
                <TenantResourcesChip
                  tenantId={row.id}
                  tenantName={row.displayName}
                  resources={row.resources}
                  onOpen={() => onResources(row.id)}
                />
              ) : null,
          } satisfies SkyDataGridColumn<TenantView>,
        ]
      : []),
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "1%",
      renderCell: (_value, row) => (
        // One badge (round 6 A1): a word and a colour, never a colour alone. `items-start` keeps
        // the badge its own width.
        <div className="flex flex-col items-start">
          <SkyBadge appearance="tint" color={statusColor(row)}>
            {statusWord(row)}
          </SkyBadge>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: "1%",
      renderCell: (_value, row) =>
        row.deletedAt ? (
          // A deleted tenant changes no more: its one action is its offboarding list.
          <div className="flex items-center gap-1">
            <SkyButton
              appearance="subtle"
              size="small"
              onClick={() => onOffboarding(row)}
              aria-label={`Offboarding list for ${row.displayName}`}
              testId={`tenant-offboarding-${row.id}`}
            >
              Offboarding list
            </SkyButton>
          </div>
        ) : (
          // #1071: one narrow pinned column. Edit, the most common action, stays a button; the
          // rest are in the menu beside it, so Actions is about 150 px, not 340.
          <div className="flex items-center justify-end gap-1">
            <SkyButton
              appearance="subtle"
              size="small"
              icon={EditIcon}
              onClick={() => onEdit(row)}
              disabled={modeUnknown}
              aria-label={`Edit ${row.displayName}`}
            >
              Edit
            </SkyButton>
            <SkyMenu
              align="end"
              trigger={
                <SkyButton
                  appearance="subtle"
                  size="small"
                  testId={`tenant-actions-${row.id}`}
                  aria-label={`More actions for ${row.displayName}`}
                >
                  ⋯
                </SkyButton>
              }
              items={[
                {
                  key: "connectors",
                  label: "Connectors",
                  onClick: () => onConnectors(row),
                },
                {
                  key: "toggle",
                  label: row.status === "active" ? "Disable" : "Enable",
                  onClick: () => onToggle(row),
                },
                // F32 PR3: only a disabled tenant can be deleted.
                ...(row.status === "disabled"
                  ? [
                      {
                        key: "delete",
                        label: "Delete",
                        onClick: () => onDelete(row),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ),
    },
  ];
}
